import { BaseEntity } from "../../core/entity/BaseEntity";
import Entity from "../../core/entity/Entity";
import { on } from "../../core/entity/handler";
import { createRigid3D } from "../../core/physics/body/bodyFactories";
import { RevoluteConstraint3D } from "../../core/physics/constraints/RevoluteConstraint3D";
import { Box } from "../../core/physics/shapes/Box";
import { V, V2d } from "../../core/Vector";
import { RigConfig } from "./BoatConfig";
import { Hull } from "./Hull";
import { Sail } from "./sail/Sail";

const GRAVITY = 32.174; // ft/s²

export class Rig extends BaseEntity {
  layer = "boat" as const;
  body: NonNullable<Entity["body"]>;
  private boomConstraint: RevoluteConstraint3D;
  sail!: Sail;

  private mastPosition: V2d;
  private boomLength: number;
  private boomWidth: number;
  private boomMass: number;
  private boomZ: number;
  private mastColor: number;
  private boomColor: number;
  private mastTopZ: number;
  private stays: RigConfig["stays"];

  constructor(
    readonly hull: Hull,
    config: RigConfig,
  ) {
    super();

    const { mastPosition, boomLength, boomWidth, boomMass, colors, mainsail } =
      config;

    this.mastPosition = mastPosition;
    this.boomLength = boomLength;
    this.boomWidth = boomWidth;
    this.boomMass = boomMass;
    this.boomZ = mainsail.zFoot ?? 3;
    this.mastTopZ = mainsail.zHead ?? 20;
    this.mastColor = colors.mast;
    this.boomColor = colors.boom;
    this.stays = config.stays;

    // Boom physics body — 6DOF so the 3D revolute joint can lock its
    // orientation to the hull's (hinge around the mast axis). Pivot is at
    // origin, boom extends in -x direction.
    const [mastWorldX, mastWorldY, mastWorldZ] = hull.body.toWorldFrame3D(
      mastPosition.x,
      mastPosition.y,
      this.boomZ,
    );
    // Moments of inertia about the pivot end (the body's origin is at the
    // mast, so roll/pitch rotate about the pivot, not the boom's CoM).
    // For a uniform rod of length L pivoted at one end: I = m L² / 3.
    // Roll is about the boom's long axis, so it's dominated by width, not length.
    const pitchInertia = (boomMass * boomLength * boomLength) / 3;
    const rollInertia = (boomMass * boomWidth * boomWidth) / 3;
    this.body = createRigid3D({
      motion: "dynamic",
      mass: boomMass,
      position: [mastWorldX, mastWorldY],
      rollInertia,
      pitchInertia,
      zMass: boomMass,
      z: mastWorldZ,
    });
    this.body.addShape(new Box({ width: boomLength, height: boomWidth }), [
      -boomLength / 2,
      0,
    ]);

    // 3D revolute joint (hinge around the mast axis): pins the boom to the
    // hull at the mast pivot in 3D and locks the boom's roll/pitch to the
    // hull's, leaving only yaw around the mast axis free.
    this.boomConstraint = new RevoluteConstraint3D(hull.body, this.body, {
      localPivotA: [mastPosition.x, mastPosition.y, this.boomZ],
      localPivotB: [0, 0, 0],
      collideConnected: false,
    });

    this.constraints = [this.boomConstraint];

    // Create mainsail
    this.sail = this.addChild(
      new Sail({
        ...mainsail,
        getHeadPosition: () => this.getMastWorldPosition(),
        headLocalPosition: mastPosition,
        getClewPosition: () => this.getBoomEndWorldPosition(),
        headConstraint: { body: hull.body, localAnchor: mastPosition },
        clewConstraint: { body: this.body, localAnchor: V(-boomLength, 0) },
        getHullBody: () => this.hull.body,
      }),
    );
  }

  @on("tick")
  onTick() {
    // Gravity at the boom's CoM (midpoint of the rod along its local x-axis).
    // The revolute joint locks roll/pitch to the hull, so the resulting
    // moment transfers through the pivot and heels the hull based on boom
    // yaw — which is the physically meaningful effect of boom weight.
    if (this.boomMass > 0) {
      this.body.applyForce3D(
        0,
        0,
        -this.boomMass * GRAVITY,
        -this.boomLength / 2,
        0,
        0,
      );
    }
  }

  /** Hull-local mast position. */
  getMastPosition(): V2d {
    return this.mastPosition;
  }

  /** Z-height of mast top. */
  getMastTopZ(): number {
    return this.mastTopZ;
  }

  /** Z-height of the boom. */
  getBoomZ(): number {
    return this.boomZ;
  }

  /** Boom width in ft. */
  getBoomWidth(): number {
    return this.boomWidth;
  }

  /** Mast visual color. */
  getMastColor(): number {
    return this.mastColor;
  }

  /** Boom visual color. */
  getBoomColor(): number {
    return this.boomColor;
  }

  /** Stay attachment config. */
  getStays(): typeof this.stays {
    return this.stays;
  }

  getMastWorldPosition(): V2d {
    const [x, y] = this.hull.body.position;
    return this.mastPosition.rotate(this.hull.body.angle).iadd([x, y]);
  }

  getBoomEndWorldPosition(): V2d {
    // Use the boom body's full 3D transform. The 3D revolute joint keeps
    // the boom body's orientation and z in sync with the hull's tilt, so
    // this naturally includes hull roll/pitch parallax.
    const [x, y] = this.body.toWorldFrame3D(-this.boomLength, 0, 0);
    return V(x, y);
  }

  getBoomLength(): number {
    return this.boomLength;
  }

  /**
   * Boom yaw relative to hull (around the hull's z-axis), maintained by
   * the 3D revolute joint via axis projection. Prefer this over
   * `boom.angle - hull.angle` — with hull tilt, that extraction mixes yaw
   * with tilt due to atan2 projection.
   */
  getBoomRelativeYaw(): number {
    return this.boomConstraint.getRelativeAngle();
  }
}
